/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Media Value Model
   Strongly-typed image and media representations with explicit
   metadata, color space, alpha semantics, and resource ownership.
   ═══════════════════════════════════════════════════════════════ */

import type { TextureHandle } from './types';
import { generateStableId } from '../domain/id';

export type ImageFormat = 'rgba8' | 'rgba16f' | 'rgba32f';
export type ColorSpace = 'srgb' | 'display-p3' | 'linear';
export type AlphaMode = 'straight' | 'premultiplied' | 'opaque';
export type MediaSourceType = 'upload' | 'generated' | 'processed';

export interface ImageMetadata {
  /** Width in pixels */
  readonly width: number;
  /** Height in pixels */
  readonly height: number;
  /** Aspect ratio (width / height) */
  readonly aspectRatio: number;
  /** Color channel format */
  readonly format: ImageFormat;
  /** Color space tag (defaults to sRGB in browser WebGL) */
  readonly colorSpace: ColorSpace;
  /** Alpha presence and encoding mode */
  readonly alphaMode: AlphaMode;
  /** Origin source category */
  readonly sourceType: MediaSourceType;
  /** Original untruncated source dimensions if downscaled for GPU */
  readonly sourceDimensions?: { readonly width: number; readonly height: number };
  /** Original unscaled source element or blob if available */
  readonly originalSource?: unknown;
}

export interface ImageValue {
  readonly id: string;
  readonly texture: TextureHandle;
  readonly metadata: ImageMetadata;
  dispose(): void;
}

export function createImageValue(
  texture: TextureHandle,
  options?: Partial<ImageMetadata>
): ImageValue {
  const width = options?.width ?? texture.width;
  const height = options?.height ?? texture.height;
  const aspectRatio = height > 0 ? width / height : 1;

  const metadata: ImageMetadata = {
    width,
    height,
    aspectRatio,
    format: options?.format ?? 'rgba8',
    colorSpace: options?.colorSpace ?? 'srgb',
    alphaMode: options?.alphaMode ?? 'straight',
    sourceType: options?.sourceType ?? 'processed',
    sourceDimensions: options?.sourceDimensions,
    originalSource: options?.originalSource,
  };

  let isDisposed = false;

  return {
    id: generateStableId('img'),
    texture,
    metadata,
    dispose: () => {
      if (!isDisposed) {
        isDisposed = true;
        texture.dispose();
      }
    },
  };
}

/**
 * Extracts a TextureHandle safely whether input is an ImageValue or raw TextureHandle (backward compatibility).
 */
export function getTextureFromMedia(input: unknown): TextureHandle | null {
  if (!input || typeof input !== 'object') return null;
  if ('texture' in input && (input as ImageValue).texture) {
    return (input as ImageValue).texture;
  }
  if ('nativeHandle' in input) {
    return input as TextureHandle;
  }
  return null;
}

/**
 * Extracts width and height safely from ImageValue or TextureHandle.
 */
export function getMediaDimensions(input: unknown): { width: number; height: number } {
  if (!input || typeof input !== 'object') return { width: 512, height: 512 };
  if ('metadata' in input) {
    const meta = (input as ImageValue).metadata;
    return { width: meta.width, height: meta.height };
  }
  if ('width' in input && 'height' in input) {
    const tex = input as TextureHandle;
    return { width: tex.width || 512, height: tex.height || 512 };
  }
  return { width: 512, height: 512 };
}
